import { Component, Input, forwardRef, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { Country } from 'src/app/models/country';

const ALL_SENTINEL = 'ALL';

@Component({
  selector: 'app-country-multi-select',
  templateUrl: './country-multi-select.component.html',
  styleUrls: ['./country-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryMultiSelectComponent),
      multi: true
    }
  ]
})
export class CountryMultiSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() countries: Country[] = [];
  @Input() label = 'Select Countries';
  @Input() showAllOption = true;
  @Input() floatLabel: 'always' | 'auto' | 'never' = 'auto';
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';

  selectedIsos: string[] = [];
  isDisabled = false;

  private prevAllToggled = false;
  private onChange: (value: Country[]) => void = () => {};
  private onTouched: () => void = () => {};
  private pendingValue: Country[] | null = null;

  get isAllSelected(): boolean {
    return this.selectedIsos.includes(ALL_SENTINEL);
  }

  get displayText(): string {
    if (this.isAllSelected) {
      return 'All countries';
    }
    return this.countries
      .filter(c => this.selectedIsos.includes(c.iso2))
      .map(c => c.name)
      .join(', ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['countries'] && this.countries.length > 0 && this.pendingValue !== null) {
      this.applyValue(this.pendingValue);
      this.pendingValue = null;
    }
  }

  onSelectionChange(event: MatSelectChange): void {
    const newValues: string[] = event.value;
    const nowHasAll = newValues.includes(ALL_SENTINEL);
    const hadAll = this.prevAllToggled;
    const allIsos = this.countries.map(c => c.iso2);

    if (nowHasAll && !hadAll) {
      this.selectedIsos = [ALL_SENTINEL, ...allIsos];
    } else if (!nowHasAll && hadAll) {
      this.selectedIsos = [];
    } else if (nowHasAll) {
      // ALL was selected but user unchecked an individual country
      this.selectedIsos = newValues.filter(v => v !== ALL_SENTINEL);
    } else {
      const isos = newValues.filter(v => v !== ALL_SENTINEL);
      if (isos.length === allIsos.length && allIsos.length > 0) {
        this.selectedIsos = [ALL_SENTINEL, ...isos];
      } else {
        this.selectedIsos = isos;
      }
    }

    this.prevAllToggled = this.selectedIsos.includes(ALL_SENTINEL);
    this.emitValue();
  }

  writeValue(value: Country[]): void {
    if (this.countries.length === 0 && value && value.length > 0) {
      this.pendingValue = value;
      return;
    }
    this.applyValue(value);
  }

  registerOnChange(fn: (value: Country[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  private applyValue(value: Country[]): void {
    if (!value || value.length === 0) {
      this.selectedIsos = [];
    } else {
      const isos = value.map(c => c.iso2);
      if (this.countries.length > 0 && isos.length === this.countries.length) {
        this.selectedIsos = [ALL_SENTINEL, ...isos];
      } else {
        this.selectedIsos = isos;
      }
    }
    this.prevAllToggled = this.selectedIsos.includes(ALL_SENTINEL);
  }

  private emitValue(): void {
    const selected = this.countries.filter(c => this.selectedIsos.includes(c.iso2));
    this.onChange(selected);
    this.onTouched();
  }
}
